import { useMemo } from 'react';
import { useTranslation } from '~/_shared/utils/hooks';
import { useIsConnectedLayeredMapSelector } from '~/store/mapInfo/mapInfo.selectors';
import { standardBaseMapStructure } from './baseMap/standardBaseMap/standardBaseMap.structure';
import { type BaseMapStructureProps } from './baseMap/standardBaseMap/standardBaseMap.types';
import { mapMarkersGraphicsStructure } from './mapMarkersGraphics/mapMarkersGraphics.structure';
import { type MapMarkersGraphicsSettingsStructureProps } from './mapMarkersGraphics/mapMarkersGraphics.types';
import { otherSettingsStructure } from './otherSettings/otherSettings.structure';
import { type OtherSettingsStructureProps } from './otherSettings/otherSettings.types';
import { type SharedPresentationalMapsStructureProps } from './sharedPresentationalMaps/sharedPresentationalMaps.types';
import { useSharedPresentationalMapsStructure } from './sharedPresentationalMaps/useSharedPresentationalMapsStructure';

export const useMapSettingsAccordionStructure = ({
  baseMapProps,
  mapMarkersGraphicsProps,
  sharedPresentationMapProps,
  otherSettingsProps,
}: {
  baseMapProps: BaseMapStructureProps;
  mapMarkersGraphicsProps: MapMarkersGraphicsSettingsStructureProps;
  sharedPresentationMapProps: SharedPresentationalMapsStructureProps;
  otherSettingsProps: OtherSettingsStructureProps;
}) => {
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();
  const sharedPresentationalMapsStructure = useSharedPresentationalMapsStructure(sharedPresentationMapProps);
  const [t] = useTranslation();

  return useMemo(() => ([
    {
      title: t('Map Markers / Graphics').toUpperCase(),
      subItems: mapMarkersGraphicsStructure(t, mapMarkersGraphicsProps, isLayeredMapConnected),
    },
    {
      title: t('Base Map').toUpperCase(),
      subItems: standardBaseMapStructure(t, baseMapProps),
    },
    {
      title: t('Shared/Display Maps').toUpperCase(),
      subItems: sharedPresentationalMapsStructure,
    },
    {
      title: t('Other Settings').toUpperCase(),
      subItems: otherSettingsStructure(t, otherSettingsProps),
    },
  ]), [baseMapProps, isLayeredMapConnected, mapMarkersGraphicsProps, otherSettingsProps, sharedPresentationalMapsStructure, t]);
};
